import React, { useState, useEffect, useRef, useCallback } from 'react';
import { activityLogAPI, noteAPI, userAPI } from '../../../services/api';
import { useAuth } from '../../../AuthContext';

const activityConfig = {
    0: { label: 'Task Completed', icon: 'fa-check-circle', color: '#27ae60' },
    1: { label: 'Task Created', icon: 'fa-plus-circle', color: '#3498db' },
    2: { label: 'Status Changed', icon: 'fa-exchange-alt', color: '#f39c12' },
    3: { label: 'Document Uploaded', icon: 'fa-file-upload', color: '#8e44ad' },
    4: { label: 'Photo Uploaded', icon: 'fa-camera', color: '#e67e22' },
    5: { label: 'Note Added', icon: 'fa-sticky-note', color: '#1abc9c' },
    6: { label: 'Task Cancelled', icon: 'fa-times-circle', color: '#e74c3c' },
    // Customer email sent by an outside system — Ground Control, once it is the sole
    // sender for repair correspondence. Without these two, both fell through to the
    // "Note Added" default and appeared in the timeline mislabelled as notes.
    //
    // Pending is amber and "Scheduled" because it has not happened yet: the whole
    // reason for recording it is so nobody phones a customer an hour before an email
    // they do not know is coming.
    7: { label: 'Email Scheduled', icon: 'fa-clock', color: '#f39c12' },
    8: { label: 'Email Sent', icon: 'fa-paper-plane', color: '#2980b9' },
};

const filterOptions = [
    { key: null, label: 'All' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'documents', label: 'Docs/Photos' },
    { key: 'status', label: 'Status' },
    { key: 'notes', label: 'Notes' },
];

const ActivityTab = ({ project }) => {
    const { user } = useAuth();
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const [activeFilter, setActiveFilter] = useState(null);

    // Note composer state
    const [noteContent, setNoteContent] = useState('');
    const [taggedUserIds, setTaggedUserIds] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionResults, setMentionResults] = useState([]);
    const [mentionIndex, setMentionIndex] = useState(0);
    const [postingNote, setPostingNote] = useState(false);
    const textareaRef = useRef(null);
    const mentionDropdownRef = useRef(null);

    // Fetch users once on mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await userAPI.getAll();
                setAllUsers(response.data);
            } catch (err) {
                console.error('Error fetching users:', err);
            }
        };
        fetchUsers();
    }, []);

    const fetchActivities = useCallback(async (filter) => {
        if (!project?.id) return;
        setLoadingActivities(true);
        try {
            const response = await activityLogAPI.getByProject(project.id, filter);
            setActivities(response.data);
        } catch (err) {
            console.error('Error fetching activity logs:', err);
        } finally {
            setLoadingActivities(false);
        }
    }, [project?.id]);

    useEffect(() => {
        fetchActivities(activeFilter);
    }, [fetchActivities, activeFilter]);

    // Handle @ mention detection
    const handleNoteChange = (e) => {
        const value = e.target.value;
        setNoteContent(value);

        const cursorPos = e.target.selectionStart;
        const textBefore = value.substring(0, cursorPos);
        const atMatch = textBefore.match(/@(\w*)$/);

        if (atMatch) {
            const query = atMatch[1].toLowerCase();
            setMentionQuery(query);
            const filtered = allUsers.filter(u =>
                u.username.toLowerCase().includes(query) && u.id !== user?.id
            );
            setMentionResults(filtered.slice(0, 5));
            setMentionIndex(0);
        } else {
            setMentionQuery(null);
            setMentionResults([]);
        }
    };

    const insertMention = (selectedUser) => {
        const textarea = textareaRef.current;
        const cursorPos = textarea.selectionStart;
        const textBefore = noteContent.substring(0, cursorPos);
        const textAfter = noteContent.substring(cursorPos);
        const atIndex = textBefore.lastIndexOf('@');
        const newText = textBefore.substring(0, atIndex) + `@${selectedUser.username} ` + textAfter;

        setNoteContent(newText);
        if (!taggedUserIds.includes(selectedUser.id)) {
            setTaggedUserIds([...taggedUserIds, selectedUser.id]);
        }
        setMentionQuery(null);
        setMentionResults([]);

        // Refocus textarea
        setTimeout(() => {
            const newPos = atIndex + selectedUser.username.length + 2;
            textarea.focus();
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (mentionResults.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => Math.min(prev + 1, mentionResults.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                insertMention(mentionResults[mentionIndex]);
            } else if (e.key === 'Escape') {
                setMentionQuery(null);
                setMentionResults([]);
            }
        }
    };

    const handlePostNote = async () => {
        if (!noteContent.trim() || !project?.id) return;
        setPostingNote(true);
        try {
            await noteAPI.create({
                projectId: project.id,
                content: noteContent.trim(),
                taggedUserIds
            });
            setNoteContent('');
            setTaggedUserIds([]);
            fetchActivities(activeFilter);
        } catch (err) {
            console.error('Error posting note:', err);
        } finally {
            setPostingNote(false);
        }
    };

    if (!project) return null;

    const milestones = [
        { label: 'Project Created', date: project.createdAt },
        { label: 'Estimate Date', date: project.estimateDate },
        { label: 'Contract Signed', date: project.contractSignedDate },
        { label: 'Scheduled Start', date: project.scheduledStartDate },
        { label: 'Completion', date: project.completionDate },
    ];

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Not set';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    const formatTimestamp = (dateStr) => {
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
        });
    };

    // Render note content with highlighted @mentions
    const renderNoteContent = (content) => {
        if (!content) return null;
        const parts = content.split(/(@\w+)/g);
        return parts.map((part, i) => {
            if (part.startsWith('@')) {
                return (
                    <span key={i} style={{
                        backgroundColor: 'rgba(26, 188, 156, 0.15)',
                        color: '#1abc9c',
                        fontWeight: 600,
                        padding: '1px 4px',
                        borderRadius: '3px'
                    }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    return (
        <div>
            <div className="tab-header">
                <h3>Activity</h3>
            </div>

            {/* Milestone Timeline */}
            <div className="activity-timeline">
                {milestones.map((event, idx) => (
                    <div className="activity-item" key={idx}>
                        <div className={`activity-dot${!event.date ? ' future' : ''}`} />
                        <div className="activity-event">{event.label}</div>
                        <div className="activity-date">{formatDate(event.date)}</div>
                    </div>
                ))}
            </div>

            {/* Note Composer */}
            <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'var(--bg-tertiary)',
                borderRadius: '8px',
                border: '1px solid var(--border-primary)'
            }}>
                <div style={{ position: 'relative' }}>
                    <textarea
                        ref={textareaRef}
                        value={noteContent}
                        onChange={handleNoteChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Add a note... Use @ to mention a team member"
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid var(--border-input)',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-primary)'
                        }}
                        maxLength={2000}
                    />
                    {/* Mention dropdown */}
                    {mentionResults.length > 0 && (
                        <div ref={mentionDropdownRef} style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-secondary)',
                            borderRadius: '6px',
                            boxShadow: 'var(--shadow-dropdown)',
                            zIndex: 10,
                            width: '220px',
                            maxHeight: '180px',
                            overflowY: 'auto'
                        }}>
                            {mentionResults.map((u, idx) => (
                                <div
                                    key={u.id}
                                    onClick={() => insertMention(u)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: idx === mentionIndex ? 'var(--bg-tertiary)' : 'transparent',
                                        color: 'var(--text-primary)',
                                        fontSize: '0.85rem',
                                        fontWeight: idx === mentionIndex ? 600 : 400,
                                        borderBottom: idx < mentionResults.length - 1 ? '1px solid var(--border-light)' : 'none'
                                    }}
                                >
                                    @{u.username}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {noteContent.length}/2000
                        {taggedUserIds.length > 0 && ` | ${taggedUserIds.length} tagged`}
                    </span>
                    <button
                        onClick={handlePostNote}
                        disabled={!noteContent.trim() || postingNote}
                        style={{
                            padding: '8px 20px',
                            background: noteContent.trim() ? 'linear-gradient(45deg, var(--surface-active), var(--surface-active-hover))' : 'var(--text-faint)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: noteContent.trim() ? 'pointer' : 'not-allowed',
                            opacity: postingNote ? 0.7 : 1
                        }}
                    >
                        {postingNote ? 'Posting...' : 'Post Note'}
                    </button>
                </div>
            </div>

            {/* Filter Pills */}
            <div style={{
                marginTop: '1.5rem',
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
            }}>
                {filterOptions.map(opt => (
                    <button
                        key={opt.key || 'all'}
                        onClick={() => setActiveFilter(opt.key)}
                        style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            border: activeFilter === opt.key ? '2px solid var(--accent-primary)' : '1px solid var(--border-secondary)',
                            background: activeFilter === opt.key ? 'var(--surface-active)' : 'var(--bg-secondary)',
                            color: activeFilter === opt.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Recent Activity Feed */}
            <div style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>Recent Activity</h4>
                {loadingActivities ? (
                    <p style={{ color: 'var(--text-muted)' }}>Loading activity...</p>
                ) : activities.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        {activeFilter
                            ? `No ${filterOptions.find(f => f.key === activeFilter)?.label.toLowerCase()} activity found.`
                            : 'No activity recorded yet. Actions like completing tasks, uploading documents, and changing project status will appear here.'
                        }
                    </p>
                ) : (
                    <div className="activity-feed">
                        {activities.map((activity) => {
                            const config = activityConfig[activity.activityType] || activityConfig[5];
                            const isNote = activity.activityType === 5 && activity.note;
                            return (
                                <div key={activity.id} style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem',
                                    padding: '0.75rem 0',
                                    borderBottom: '1px solid var(--border-light)'
                                }}>
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: config.color,
                                        marginTop: '6px',
                                        flexShrink: 0
                                    }} />
                                    <i className={`fas ${config.icon}`} style={{
                                        color: config.color,
                                        fontSize: '1rem',
                                        marginTop: '2px',
                                        width: '20px',
                                        textAlign: 'center',
                                        flexShrink: 0
                                    }} />
                                    <div style={{ flex: 1 }}>
                                        {isNote ? (
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                                                {renderNoteContent(activity.note.content)}
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                                {activity.description}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                                            {isNote
                                                ? (activity.note.author?.username || activity.performedBy?.username || 'System')
                                                : (activity.performedBy?.username || 'System')
                                            }
                                            {' \u00b7 '}
                                            {formatTimestamp(activity.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityTab;
